import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const articleLinks = [
  { name: 'Complete Guide', path: '/litbuy-spreadsheet-guide' },
  { name: 'Beginner Guide', path: '/litbuy-spreadsheet-beginners' },
  { name: 'Features Overview', path: '/litbuy-spreadsheet-features' },
  { name: 'Templates Guide', path: '/litbuy-spreadsheet-templates' },
  { name: 'Pricing Guide', path: '/litbuy-spreadsheet-pricing' },
  { name: 'Safety & Security', path: '/litbuy-spreadsheet-safety' },
  { name: 'Pros and Cons', path: '/litbuy-spreadsheet-pros-cons' },
  { name: 'Alternatives', path: '/litbuy-spreadsheet-alternatives' },
  { name: 'Advanced Tips', path: '/litbuy-spreadsheet-advanced-tips' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [catDropdownOpen, setCatDropdownOpen] = useState(false);
  const [blogDropdownOpen, setBlogDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [mobileBlogOpen, setMobileBlogOpen] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const blogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (catRef.current && !catRef.current.contains(event.target as Node)) {
        setCatDropdownOpen(false);
      }
      if (blogRef.current && !blogRef.current.contains(event.target as Node)) {
        setBlogDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isTransparent = !scrolled && !mobileMenuOpen;

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isTransparent
        ? 'bg-transparent'
        : 'bg-background-50/95 backdrop-blur-md shadow-sm'
    }`}>
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-[4.5rem]">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
            <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-lg bg-primary-500">
              <i className="ri-file-list-3-line text-white text-base md:text-lg"></i>
            </div>
            <span className={`font-heading text-lg md:text-xl font-semibold transition-colors duration-300 ${
              isTransparent ? 'text-white' : 'text-foreground-900'
            }`}>Litbuy Spreadsheets</span>
          </Link>

          <div className="hidden md:flex items-center gap-7 lg:gap-9">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isTransparent ? 'text-white/90 hover:text-white' : 'text-foreground-700 hover:text-primary-500'
              }`}
            >
              Home
            </Link>

            <div className="relative" ref={catRef}>
              <button
                onClick={() => { setCatDropdownOpen(!catDropdownOpen); setBlogDropdownOpen(false); }}
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isTransparent ? 'text-white/90 hover:text-white' : 'text-foreground-700 hover:text-primary-500'
                }`}
              >
                Categories
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${catDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {catDropdownOpen && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-60 bg-background-50 rounded-xl shadow-lg border border-background-200/70 py-2 max-h-80 overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setCatDropdownOpen(false)}
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="relative" ref={blogRef}>
              <button
                onClick={() => { setBlogDropdownOpen(!blogDropdownOpen); setCatDropdownOpen(false); }}
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isTransparent ? 'text-white/90 hover:text-white' : 'text-foreground-700 hover:text-primary-500'
                }`}
              >
                SEO Guides
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${blogDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {blogDropdownOpen && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-56 bg-background-50 rounded-xl shadow-lg border border-background-200/70 py-2 max-h-72 overflow-y-auto z-50">
                  {articleLinks.map((article) => (
                    <Link
                      key={article.path}
                      to={article.path}
                      onClick={() => setBlogDropdownOpen(false)}
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {article.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/litbuy-spreadsheet-guide"
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isTransparent ? 'text-white/90 hover:text-white' : 'text-foreground-700 hover:text-primary-500'
              }`}
            >
              Shopping Guide
            </Link>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${
                isTransparent
                  ? 'bg-white/15 backdrop-blur-sm text-white border border-white/30 hover:bg-white/25'
                  : 'bg-primary-500 text-white hover:bg-primary-600'
              }`}
            >
              Buy It Now
              <i className="ri-arrow-right-up-line text-xs"></i>
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-9 h-9 flex items-center justify-center cursor-pointer transition-colors ${
              isTransparent ? 'text-white' : 'text-foreground-700'
            }`}
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t border-background-200/70 py-4 px-2 bg-background-50 rounded-b-xl shadow-lg">
            <div className="flex flex-col gap-1">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className="px-3 py-3 text-sm font-semibold text-foreground-700 hover:bg-background-100 rounded-lg cursor-pointer flex items-center gap-2">
                <i className="ri-home-4-line text-primary-500"></i> Home
              </Link>

              <div>
                <button
                  onClick={() => setMobileCatOpen(!mobileCatOpen)}
                  className="w-full px-3 py-3 text-sm font-semibold text-foreground-700 hover:bg-background-100 rounded-lg cursor-pointer flex items-center justify-between"
                >
                  <span className="flex items-center gap-2">
                    <i className="ri-store-2-line text-accent-500"></i> Categories
                  </span>
                  <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {mobileCatOpen && (
                  <div className="ml-3 mt-1 border-l-2 border-background-200 pl-3 flex flex-col max-h-56 overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={() => { setMobileMenuOpen(false); setMobileCatOpen(false); }}
                        className="px-3 py-2.5 text-sm text-foreground-600 hover:text-primary-500 cursor-pointer"
                      >
                        {cat.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <button
                  onClick={() => setMobileBlogOpen(!mobileBlogOpen)}
                  className="w-full px-3 py-3 text-sm font-semibold text-foreground-700 hover:bg-background-100 rounded-lg cursor-pointer flex items-center justify-between"
                >
                  <span className="flex items-center gap-2">
                    <i className="ri-article-line text-primary-500"></i> SEO Guides
                  </span>
                  <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileBlogOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {mobileBlogOpen && (
                  <div className="ml-3 mt-1 border-l-2 border-background-200 pl-3 flex flex-col">
                    {articleLinks.map((article) => (
                      <Link
                        key={article.path}
                        to={article.path}
                        onClick={() => { setMobileMenuOpen(false); setMobileBlogOpen(false); }}
                        className="px-3 py-2.5 text-sm text-foreground-600 hover:text-primary-500 cursor-pointer"
                      >
                        {article.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/litbuy-spreadsheet-guide"
                onClick={() => setMobileMenuOpen(false)}
                className="px-3 py-3 text-sm font-semibold text-foreground-700 hover:bg-background-100 rounded-lg cursor-pointer flex items-center gap-2"
              >
                <i className="ri-shopping-bag-3-line text-accent-500"></i> Shopping Guide
              </Link>

              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="px-3 py-3 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-lg cursor-pointer flex items-center justify-center gap-1.5 mt-2"
              >
                Buy It Now <i className="ri-arrow-right-up-line text-xs"></i>
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}